'use strict';

import animationsUtility from '../utility/animations.js';

// Keep module-scoped references for cleanup
let scrollHandler = null;
let newLoadedHandler = null;
let ticking = false;
let isBound = false;

/**
 * Re-selects current container-scoped nodes each time.
 * This avoids stale references after Barba replaces the DOM.
 */
const getNodes = () => {
    const container = document.querySelector('[data-barba="container"]') || document;
    const projectsList = container.getElementById
        ? container.getElementById('tc-website-projectslist')
        : container.querySelector('#tc-website-projectslist');
    const projectsWrapper = projectsList
        ? projectsList.querySelector('#projects-wrapper')
        : null;
    return {projectsList, projectsWrapper};
};

/**
 * Scroll handler (mobile): re-query DOM and guard early if missing.
 */
const handleScroll = () => {
    if (ticking) return;
    ticking = true;

    window.requestAnimationFrame(() => {
        const {projectsWrapper} = getNodes();
        if (!projectsWrapper) {
            ticking = false;
            return;
        }
        animationsUtility.animateActiveProject(projectsWrapper);
        ticking = false;
    });
};

/**
 * Custom event handler for "new-loaded-projects".
 * Always re-query DOM and guard against nulls.
 */
const handleNewLoadedProjects = () => {
    const {projectsList, projectsWrapper} = getNodes();
    if (!projectsList || !projectsWrapper) return;

    const initialHeight = projectsWrapper.offsetHeight;

    // Let the DOM paint the new items before measuring - use requestAnimationFrame for Safari
    const processNewProjects = () => {
        const hiddenProjects = projectsList.querySelectorAll('.project-item.hidden');
        if (hiddenProjects.length === 0) {
            return;
        }

        const listType = projectsWrapper.classList.contains('grid') ? 'grid' : 'list';
        const sample = projectsList.querySelector('.project-item:not(:first-child)');
        if (!sample) {
            return;
        }

        let projectItemHeight = sample.offsetHeight;

        // Responsive adjustments
        if (window.matchMedia('(min-width: 575px) and (max-width: 767px)').matches) {
            projectItemHeight = (projectItemHeight * 2) + 20;
        } else if (window.matchMedia('(max-width: 575px)').matches) {
            projectItemHeight = (projectItemHeight * 4) + 60;
        }

        const newHeight = listType === 'grid'
            ? initialHeight + projectItemHeight + 20
            : initialHeight + (projectItemHeight * 4);

        animationsUtility.animateProjectsListContainerHeight(projectsWrapper, initialHeight, newHeight);
        animationsUtility.animateProjectsList(listType, hiddenProjects);
    };

    // Use requestAnimationFrame for more reliable timing on Safari
    if (window.requestAnimationFrame) {
        window.requestAnimationFrame(processNewProjects);
    } else {
        setTimeout(processNewProjects, 16); // Fallback for older browsers
    }
};

const init = () => {
    const {projectsList} = getNodes();
    if (!projectsList) return;

    // Detect history back
    window.addEventListener('popstate', () => {
        sessionStorage.setItem('keepData', 'true')
    })

    // Data housekeeping
    if (sessionStorage.getItem('keepData') !== 'true') {
        sessionStorage.removeItem('selectedFilters');
        sessionStorage.removeItem('tc-projects-scroll');
    } else {
        sessionStorage.removeItem('keepData');
    }

    // Mobile scroll binding (with cleanup support)
    if (window.innerWidth < 768 && !scrollHandler) {
        scrollHandler = handleScroll;
        window.addEventListener('scroll', scrollHandler, {passive: true});
    }

    // Custom event binding
    if (!newLoadedHandler) {
        newLoadedHandler = handleNewLoadedProjects;
        window.addEventListener('new-loaded-projects', newLoadedHandler);
    }

    isBound = true;
};

const destroy = () => {
    // Remove listeners if they were set
    if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
    }
    if (newLoadedHandler) {
        window.removeEventListener('new-loaded-projects', newLoadedHandler);
        newLoadedHandler = null;
    }
    ticking = false;
    isBound = false;
};

export default {init, destroy};
